import { create } from 'zustand';

interface FormState {
  // Event data
  event: any | null;
  theme: any | null;
  
  // Form values
  formData: Record<string, any>;
  selectedCategoryId: string | null;
  selectedShippingId: string | null;
  merchandiseItems: { itemId: string; quantity: number }[];
  participants: Record<string, any>[];
  
  // UI state
  currentStep: number;
  isSubmitting: boolean;
  errors: Record<string, string>;
  
  // Pricing
  subtotal: number;
  shippingFee: number;
  merchandiseTotal: number;
  totalAmount: number;
  
  // Actions
  setEvent: (event: any) => void;
  setTheme: (theme: any) => void;
  setFormValue: (fieldId: string, value: any) => void;
  setFormData: (data: Record<string, any>) => void;
  setCategory: (categoryId: string) => void;
  setShipping: (shippingId: string) => void;
  setMerchandiseQuantity: (itemId: string, quantity: number) => void;
  addParticipant: () => void;
  removeParticipant: (index: number) => void;
  setParticipantValue: (index: number, fieldId: string, value: any) => void;
  setCurrentStep: (step: number) => void;
  nextStep: () => void;
  prevStep: () => void;
  setError: (fieldId: string, error: string) => void;
  clearError: (fieldId: string) => void;
  clearAllErrors: () => void;
  setSubmitting: (isSubmitting: boolean) => void;
  calculatePricing: () => void;
  reset: () => void;
}

/** Mix a hex color toward white (amount 0..1). */
function lighten(hex: string, amount: number): string {
  const h = hex.replace('#', '');
  if (h.length !== 6) return '#EBEFF5';
  const n = parseInt(h, 16);
  const mix = (c: number) => Math.round(c + (255 - c) * amount);
  const r = mix(n >> 16), g = mix((n >> 8) & 255), b = mix(n & 255);
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`;
}

/** Darken a hex color toward black (amount 0..1). */
function darken(hex: string, amount: number): string {
  const h = hex.replace('#', '');
  if (h.length !== 6) return '#233550';
  const n = parseInt(h, 16);
  const mix = (c: number) => Math.round(c * (1 - amount));
  const r = mix(n >> 16), g = mix((n >> 8) & 255), b = mix(n & 255);
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`;
}

/** Set the event accent on :root. Pass nothing to restore the portal default. */
export function applyThemeColors(primary?: string | null, primaryDark?: string | null) {
  const root = document.documentElement;
  const p = primary && /^#[0-9a-fA-F]{6}$/.test(primary) ? primary : '#2E4467';
  root.style.setProperty('--color-primary', p);
  root.style.setProperty('--color-primary-dark', primaryDark && /^#[0-9a-fA-F]{6}$/.test(primaryDark) ? primaryDark : darken(p, 0.18));
  root.style.setProperty('--color-primary-soft', lighten(p, 0.88));
}

const initialState = {
  event: null,
  theme: null,
  formData: {},
  selectedCategoryId: null,
  selectedShippingId: null,
  merchandiseItems: [],
  participants: [{}],
  currentStep: 0,
  isSubmitting: false,
  errors: {},
  subtotal: 0,
  shippingFee: 0,
  merchandiseTotal: 0,
  totalAmount: 0,
};

export const useFormStore = create<FormState>((set, get) => ({
  ...initialState,

  setEvent: (event) => set({ event }),

  setTheme: (theme) => {
    applyThemeColors(theme?.primaryColor, theme?.primaryDark);
    set({ theme });
  },

  setFormValue: (fieldId, value) =>
    set((state) => ({
      formData: { ...state.formData, [fieldId]: value },
    })),

  setFormData: (data) =>
    set((state) => ({
      formData: { ...state.formData, ...data },
    })),

  setCategory: (categoryId) => {
    set({ selectedCategoryId: categoryId });
    get().calculatePricing();
  },

  setShipping: (shippingId) => {
    set({ selectedShippingId: shippingId });
    get().calculatePricing();
  },

  setMerchandiseQuantity: (itemId, quantity) => {
    set((state) => {
      const items = [...state.merchandiseItems];
      const existingIndex = items.findIndex((i) => i.itemId === itemId);
      
      if (quantity <= 0) {
        if (existingIndex >= 0) {
          items.splice(existingIndex, 1);
        }
      } else {
        if (existingIndex >= 0) {
          items[existingIndex].quantity = quantity;
        } else {
          items.push({ itemId, quantity });
        }
      }
      
      return { merchandiseItems: items };
    });
    get().calculatePricing();
  },

  addParticipant: () =>
    set((state) => ({
      participants: [...state.participants, {}],
    })),

  removeParticipant: (index) =>
    set((state) => ({
      participants: state.participants.filter((_, i) => i !== index),
    })),

  setParticipantValue: (index, fieldId, value) =>
    set((state) => {
      const participants = [...state.participants];
      participants[index] = { ...participants[index], [fieldId]: value };
      return { participants };
    }),

  setCurrentStep: (step) => set({ currentStep: step }),

  nextStep: () =>
    set((state) => ({ currentStep: state.currentStep + 1 })),

  prevStep: () =>
    set((state) => ({ currentStep: Math.max(0, state.currentStep - 1) })),

  setError: (fieldId, error) =>
    set((state) => ({
      errors: { ...state.errors, [fieldId]: error },
    })),

  clearError: (fieldId) =>
    set((state) => {
      const errors = { ...state.errors };
      delete errors[fieldId];
      return { errors };
    }),

  clearAllErrors: () => set({ errors: {} }),

  setSubmitting: (isSubmitting) => set({ isSubmitting }),

  calculatePricing: () => {
    const state = get();
    const { event, selectedCategoryId, selectedShippingId, merchandiseItems } = state;
    
    if (!event) return;

    let subtotal = 0;
    let shippingFee = 0;
    let merchandiseTotal = 0;

    // Find category price
    const modules = event.modules || [];
    for (const module of modules) {
      if (module.type === 'race_selection' && module.settings?.categories) {
        const category = module.settings.categories.find(
          (c: any) => c.id === selectedCategoryId
        );
        if (category) {
          const unitPrice = category.discountedPrice || category.originalPrice || 0;
          const participantCount = state.participants?.length || 1;
          subtotal = unitPrice * participantCount;
        }
        
        const shipping = module.settings.shippingOptions?.find(
          (s: any) => s.id === selectedShippingId
        );
        if (shipping) {
          shippingFee = shipping.price || 0;
        }
      }
      
      // Merchandise pricing
      if (module.type === 'merchandise' && module.settings?.items) {
        for (const item of merchandiseItems) {
          const merchItem = module.settings.items.find(
            (i: any) => i.id === item.itemId
          );
          if (merchItem) {
            merchandiseTotal += (merchItem.price || 0) * item.quantity;
          }
        }
      }
    }

    const totalAmount = subtotal + shippingFee + merchandiseTotal;

    set({ subtotal, shippingFee, merchandiseTotal, totalAmount });
  },

  reset: () => set(initialState),
}));

export default useFormStore;
